import 'css/src/components/common/video-preview/SlideMarker';

import type { CSSProperties } from 'react';
import React from 'react';
import classNames from 'classnames';

const getPercentageClicked = (
    clickEvent: React.MouseEvent<HTMLElement>,
    totalWidthOfMarker: number,
): number => {
    const boundingRect = clickEvent.currentTarget.getBoundingClientRect();
    const clickedOffsetX = clickEvent.clientX - boundingRect.left;

    return Math.round((clickedOffsetX / totalWidthOfMarker) * 100);
};

type Props = {
    new_time: number;
    seek: (time: number) => void;
    style?: CSSProperties;
    className?: string;
    scene_duration: number;
    commentMarkerCallback?: (newTime: number, forceCommentingMode: boolean) => void;
    enableComments?: boolean;
    numComments?: number;
};

const SlideMarker = React.memo(function SlideMarker({
    new_time,
    seek,
    style,
    className,
    scene_duration,
    commentMarkerCallback,
    enableComments,
    numComments,
}: Props) {
    const slideMarkerRef = React.useRef<HTMLSpanElement>(null);

    const seekInternal = React.useCallback(
        (e: React.MouseEvent, percentageOffset = 100) => {
            e.preventDefault();
            e.stopPropagation();

            // if user clicks 1/3 of the way within a scene, we seek 33% into said scene
            const timeWithClickedOffset = Math.round(
                new_time + scene_duration * (percentageOffset / 100),
            );
            seek(timeWithClickedOffset);
        },
        [new_time, scene_duration, seek],
    );

    const handleScrubberClick = React.useCallback(
        (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
            if (!slideMarkerRef.current) throw new Error('slideMarkerRef is not defined');
            const totalWidthOfMarker = slideMarkerRef.current.clientWidth;
            const percentageClicked = getPercentageClicked(e, totalWidthOfMarker);
            seekInternal(e, percentageClicked);
            // call parent with new startTime to set the comment panel to current scene
            commentMarkerCallback && commentMarkerCallback(new_time, false); // args: new time, forceCommmentingMode
        },
        [commentMarkerCallback, new_time, seekInternal],
    );

    return (
        <span
            ref={slideMarkerRef}
            className={classNames('slide-marker', className)}
            style={style}
            onClick={handleScrubberClick}
        >
            {numComments !== undefined && numComments > 0 && enableComments !== false && (
                <span className="slide-marker-inner">
                    <i className="material-symbols-outlined">mode_comment</i>
                    {numComments !== undefined && numComments >= 2 && (
                        <small className="comment-count">{numComments}</small>
                    )}
                </span>
            )}
        </span>
    );
});

export default SlideMarker;
